import React from 'react';
import { useTheme, ThemePreference } from './useTheme';
import { useAppTheme, AppId } from './useAppTheme';

interface ThemeToggleProps {
  /** Optional label shown to assistive tech only. */
  ariaLabel?: string;
  /** Extra classes on the wrapping segmented control. */
  className?: string;
  /**
   * When provided, the toggle controls the **per-app** theme override
   * for this app id (saved to its own localStorage key, so an end-user
   * can disagree with the admin default for that app only).
   *
   * When omitted, falls back to the legacy global `useTheme()` hook
   * so existing call-sites stay backwards compatible.
   */
  appId?: AppId;
  /** Override API base URL when `appId` is set (defaults to `http://localhost:3001`). */
  apiBaseUrl?: string;
}

const OPTIONS: { value: ThemePreference; label: string; glyph: string }[] = [
  { value: 'system', label: 'System', glyph: '◐' },
  { value: 'light', label: 'Light', glyph: '☀' },
  { value: 'dark', label: 'Dark', glyph: '☾' },
];

/**
 * 3-state segmented control: System / Light / Dark.
 *
 * Drop into the right edge of any nav. Visual styles come from
 * the host stylesheet (rules below in tokens-adjacent CSS) so
 * each app's nav can integrate the toggle without extra imports.
 *
 * Pass `appId` to wire the toggle to the admin-controlled per-app
 * default (with a per-user override). Without `appId`, the toggle
 * controls the legacy single global preference.
 */
export function ThemeToggle({ ariaLabel = 'Theme preference', className, appId, apiBaseUrl }: ThemeToggleProps) {
  // Hooks must run unconditionally — call both and pick which result to
  // use based on whether `appId` was supplied.
  const global = useTheme();
  const perApp = useAppTheme(appId ?? 'flow-builder', { apiBaseUrl, skipFetch: !appId });
  const theme = appId ? perApp.theme : global.theme;
  const setTheme = appId ? perApp.setTheme : global.setTheme;

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={`theme-toggle${className ? ` ${className}` : ''}`}>
      {OPTIONS.map((opt) => {
        const active = theme === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.label}
            title={opt.label}
            className={`theme-toggle__btn${active ? ' theme-toggle__btn--active' : ''}`}
            onClick={() => setTheme(opt.value)}
          >
            <span aria-hidden="true">{opt.glyph}</span>
          </button>
        );
      })}
    </div>
  );
}
